import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ArrowLeft, CheckCircle2, Loader2, XCircle, Camera, AlertTriangle, PlayCircle, ChevronDown, Bug, Link2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useWorkOrder, useWoTransition } from "@/features/work-orders/api";
import { WoStatusBadge } from "@/features/work-orders/badges";
import { useActivePlansForAsset, useChecklistItems, useLinkPmPlanToWo, usePmEngineers, usePmPlanById, useSubmitPmExecution, useWoChecklistResults } from "@/features/pm/api";
import { PmPlanDialog } from "@/features/pm/PmPlanDialog";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { cn } from "@/lib/utils";
import { getSupabase } from "@/integrations/external-supabase/client";
import type { ChecklistResultRow } from "@/features/pm/types";

export const Route = createFileRoute("/_authenticated/work-orders/$woId/pm-execution")({
  component: PmExecutionPage,
  errorComponent: ({ error, reset }) => (
    <Notice
      icon={<AlertTriangle className="h-6 w-6 text-destructive" />}
      title="Couldn't load PM execution"
      body={error?.message ?? "Unexpected error."}
      action={<Button onClick={reset}>Try again</Button>}
    />
  ),
});

const ITEM_TYPE_LABEL: Record<string, string> = {
  ok_notok: "OK / Not OK",
  value: "Value",
  text: "Text",
  photo: "Photo",
};

function PmExecutionPage() {
  const { woId } = Route.useParams();
  const navigate = useNavigate();
  const woQ = useWorkOrder(woId);
  const meQ = useCurrentUser();
  const wo = woQ.data;
  const me = meQ.data;
  const planQ = usePmPlanById(wo?.pm_plan_id ?? null);
  const plan = planQ.data;
  const itemsQ = useChecklistItems(plan?.checklist_template_id ?? null);
  const items = itemsQ.data ?? [];
  const { data: existing = [] } = useWoChecklistResults(woId);
  const { data: engineers = [] } = usePmEngineers();
  const submit = useSubmitPmExecution();
  const transition = useWoTransition();

  const [results, setResults] = useState<Record<string, ChecklistResultRow>>({});
  const [datePerformed, setDatePerformed] = useState(() => new Date().toISOString().slice(0, 10));
  const [timeStarted, setTimeStarted] = useState("");
  const [timeEnded, setTimeEnded] = useState("");
  const [observations, setObservations] = useState("");
  const [nextDue, setNextDue] = useState("");
  const [supervisorId, setSupervisorId] = useState("");
  const [tab, setTab] = useState("asset");
  const [linkOpen, setLinkOpen] = useState(false);
  const [editPlanOpen, setEditPlanOpen] = useState(false);

  useEffect(() => {
    const map: Record<string, ChecklistResultRow> = {};
    for (const r of existing) map[r.checklist_item_id] = r;
    setResults(map);
  }, [existing]);

  useEffect(() => {
    if (!plan) return;
    const base = new Date((datePerformed || new Date().toISOString().slice(0, 10)) + "T00:00:00");
    base.setDate(base.getDate() + plan.frequency_days);
    setNextDue(base.toISOString().slice(0, 10));
  }, [plan, datePerformed]);

  const roles = useMemo(() => me?.roles ?? [], [me]);
  const uid = me?.user.id;
  const isAdminRole = roles.includes("super_admin") || roles.includes("biomed_head");
  const canExecute = useMemo(() => {
    if (!wo || !uid) return false;
    if (roles.includes("super_admin") || roles.includes("biomed_head") || roles.includes("facility_manager")) return true;
    if (roles.includes("biomed_engineer") && wo.assigned_to === uid) return true;
    return false;
  }, [roles, uid, wo]);

  // ───── Cascaded gates (each shows a labeled state instead of a bare spinner) ─────
  if (woQ.isLoading || meQ.isLoading) {
    return <StatusPanel label={woQ.isLoading ? "Loading work order…" : "Loading your profile…"} />;
  }
  if (woQ.error) return <Notice icon={<AlertTriangle className="h-6 w-6 text-destructive" />} title="Failed to load work order" body={woQ.error.message} />;
  if (meQ.error) return <Notice icon={<AlertTriangle className="h-6 w-6 text-destructive" />} title="Unable to load your user profile." body={meQ.error.message} />;
  if (!me) return <Notice icon={<AlertTriangle className="h-6 w-6 text-amber-500" />} title="Unable to load your user profile." body="Please sign in again." />;
  if (!wo) return <Notice icon={<AlertTriangle className="h-6 w-6 text-muted-foreground" />} title="PM work order not found" body="The work order may have been deleted, or you do not have access." action={<Button onClick={() => navigate({ to: "/work-orders" })}>Back to Work Orders</Button>} />;
  if (wo.type !== "PM") return <Notice icon={<AlertTriangle className="h-6 w-6 text-amber-500" />} title="Not a PM work order" body="This page is only available for Preventive Maintenance work orders." action={<Button onClick={() => navigate({ to: "/work-orders/$woId", params: { woId } })}>Open Work Order</Button>} />;
  if (!canExecute) return <Notice icon={<AlertTriangle className="h-6 w-6 text-amber-500" />} title="Permission required" body="You do not have permission to execute this PM work order." action={<Button onClick={() => navigate({ to: "/work-orders/$woId", params: { woId } })}>Back to Work Order</Button>} />;
  if (!wo.asset) return <Notice icon={<AlertTriangle className="h-6 w-6 text-amber-500" />} title="Asset details unavailable" body="The asset linked to this PM work order could not be loaded." />;

  // PM plan link missing → offer repair to admins
  if (!wo.pm_plan_id) {
    return (
      <div className="mx-auto max-w-md space-y-3 px-2 pb-8">
        <BackBar onBack={() => navigate({ to: "/work-orders/$woId", params: { woId } })} />
        <Notice
          icon={<AlertTriangle className="h-6 w-6 text-amber-500" />}
          title="This PM work order is not linked to a PM plan."
          body={isAdminRole ? "Link the correct PM plan for this asset to continue." : "Ask an admin to link a PM plan."}
          action={isAdminRole ? <Button onClick={() => setLinkOpen(true)}><Link2 className="mr-1 h-4 w-4" />Link PM Plan</Button> : undefined}
        />
        <DebugPanel show={isAdminRole} wo={wo} plan={null} itemsCount={0} me={me} />
        <LinkPlanDialog open={linkOpen} onOpenChange={setLinkOpen} woId={woId} assetId={wo.asset_id} />
      </div>
    );
  }
  if (planQ.isLoading) return <StatusPanel label="Loading PM plan…" />;
  if (planQ.error) return <Notice icon={<AlertTriangle className="h-6 w-6 text-destructive" />} title="Failed to load PM plan" body={planQ.error.message} />;
  if (!plan) {
    return (
      <div className="mx-auto max-w-md space-y-3 px-2 pb-8">
        <BackBar onBack={() => navigate({ to: "/work-orders/$woId", params: { woId } })} />
        <Notice
          icon={<AlertTriangle className="h-6 w-6 text-amber-500" />}
          title="PM plan unavailable"
          body="The PM plan linked to this work order could not be loaded (deleted or no access)."
          action={isAdminRole ? <Button onClick={() => setLinkOpen(true)}><Link2 className="mr-1 h-4 w-4" />Re-link PM Plan</Button> : undefined}
        />
        <DebugPanel show={isAdminRole} wo={wo} plan={null} itemsCount={0} me={me} />
        <LinkPlanDialog open={linkOpen} onOpenChange={setLinkOpen} woId={woId} assetId={wo.asset_id} />
      </div>
    );
  }
  if (!plan.checklist_template_id) {
    return (
      <div className="mx-auto max-w-md space-y-3 px-2 pb-8">
        <BackBar onBack={() => navigate({ to: "/work-orders/$woId", params: { woId } })} />
        <Notice
          icon={<AlertTriangle className="h-6 w-6 text-amber-500" />}
          title="No checklist template"
          body="No checklist template is linked to this PM plan. Please edit the PM plan and select a checklist template."
          action={isAdminRole ? <Button onClick={() => setEditPlanOpen(true)}><Pencil className="mr-1 h-4 w-4" />Edit PM Plan</Button> : undefined}
        />
        <DebugPanel show={isAdminRole} wo={wo} plan={plan} itemsCount={0} me={me} />
        <PmPlanDialog open={editPlanOpen} onOpenChange={setEditPlanOpen} plan={plan} />
      </div>
    );
  }
  if (itemsQ.isLoading) return <StatusPanel label="Loading checklist items…" />;
  if (itemsQ.error) return <Notice icon={<AlertTriangle className="h-6 w-6 text-destructive" />} title="Failed to load checklist items" body={itemsQ.error.message} />;

  const isClosed = wo.status === "Verified & Closed";
  const isResolved = wo.status === "Resolved" || isClosed;
  const isReported = wo.status === "Reported";
  const canEditChecklist = !isResolved && (wo.status === "Acknowledged" || wo.status === "In Progress");

  const updateResult = (itemId: string, patch: Partial<ChecklistResultRow>) => {
    if (!canEditChecklist) return;
    setResults((prev) => {
      const cur = prev[itemId] ?? { work_order_id: woId, checklist_item_id: itemId, result: null, value_entered: null, remarks: null, photo_url: null, completed: false };
      return { ...prev, [itemId]: { ...cur, ...patch } };
    });
  };

  const requiredCount = items.filter((i) => i.is_required).length;
  const completedRequired = items.filter((i) => i.is_required && results[i.id]?.completed).length;
  const progress = requiredCount === 0 ? 100 : Math.round((completedRequired / requiredCount) * 100);
  const criticalAsset = (wo.asset as { criticality?: string | null }).criticality === "critical";

  const handleStart = async () => {
    try {
      await transition.mutateAsync({ wo_id: woId, to: "In Progress" });
      toast.success("PM started");
      setTab("checklist");
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const notifyHeadIfOverdue = async () => {
    try {
      if (!wo.due_date) return;
      const today = new Date().toISOString().slice(0, 10);
      if (wo.due_date >= today) return;
      const sb = getSupabase();
      // assignable_engineers is restricted to super_admin / biomed_head / facility_manager;
      // for other callers this silently no-ops (best-effort).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: engineers, error: aErr } = await (sb as any).rpc("assignable_engineers", {
        p_branch_id: null,
        p_department_id: null,
        p_asset_category_id: null,
        p_work_order_id: wo.id,
      });
      if (aErr) return;
      const ids = Array.from(
        new Set(
          ((engineers ?? []) as Array<{ id: string; role: string }>)
            .filter((e) => e.role === "biomed_head")
            .map((e) => e.id),
        ),
      );
      if (ids.length === 0) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (sb as any).rpc("notify_users", {
        p_user_ids: ids,
        p_type: "pm_overdue_completed",
        p_title: `Overdue PM completed: ${wo.wo_number}`,
        p_body: `${wo.asset?.name ?? "Asset"} — PM resolved past due date ${wo.due_date}.`,
        p_link: `/work-orders/${wo.id}`,
        p_work_order_id: wo.id,
        p_priority: "high",
      });
    } catch {
      // non-fatal
    }
  };

  const handleSubmit = async () => {
    if (requiredCount > 0 && completedRequired < requiredCount) {
      toast.error(`Complete all ${requiredCount} required items first`);
      setTab("checklist");
      return;
    }
    if (criticalAsset && !supervisorId) {
      toast.error("Supervisor sign-off required for critical assets");
      setTab("completion");
      return;
    }
    try {
      await submit.mutateAsync({
        wo_id: woId,
        payload: {
          results: Object.values(results),
          observations,
          date_performed: datePerformed,
          next_due_date: nextDue,
          time_started: timeStarted || null,
          time_ended: timeEnded || null,
          supervisor_id: supervisorId || null,
        },
      });
      await notifyHeadIfOverdue();
      toast.success("PM execution submitted successfully.");
      navigate({ to: "/work-orders/$woId", params: { woId } });
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <div className="mx-auto max-w-md space-y-3 px-2 pb-28">
      <BackBar onBack={() => navigate({ to: "/work-orders/$woId", params: { woId } })} />

      <Card>
        <CardContent className="space-y-1 p-4">
          <div className="flex items-center justify-between gap-2">
            <div className="font-mono text-xs">{wo.wo_number}</div>
            <WoStatusBadge value={wo.status} />
          </div>
          <div className="text-lg font-semibold">{wo.asset?.name ?? "—"}</div>
          <div className="text-xs text-muted-foreground">
            <span className="font-mono">{wo.asset?.asset_code}</span>
            {plan.asset?.location?.name && <> · {plan.asset.location.name}</>}
            {wo.due_date && <> · Due: {wo.due_date}</>}
          </div>
          <div className="pt-1 text-xs">
            <span className="text-muted-foreground">Checklist Template: </span>
            <span className="font-medium">{plan.template?.name ?? "Not configured"}</span>
          </div>
        </CardContent>
      </Card>

      <DebugPanel show={isAdminRole} wo={wo} plan={plan} itemsCount={items.length} me={me} />


      {isReported && (
        <Alert>
          <PlayCircle className="h-4 w-4" />
          <AlertTitle>Start this PM to begin the checklist</AlertTitle>
          <AlertDescription className="flex items-center justify-between gap-2">
            <span>Status will move to “In Progress”.</span>
            <Button size="sm" onClick={handleStart} disabled={transition.isPending}>
              {transition.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Start PM
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {isResolved && (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>Read-only</AlertTitle>
          <AlertDescription>This PM has been completed. Submission is disabled.</AlertDescription>
        </Alert>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full">
          <TabsTrigger value="asset" className="flex-1">Asset</TabsTrigger>
          <TabsTrigger value="checklist" className="flex-1">Checklist</TabsTrigger>
          <TabsTrigger value="completion" className="flex-1">Completion</TabsTrigger>
        </TabsList>

        <TabsContent value="asset" className="mt-3 space-y-2">
          <Card><CardContent className="space-y-2 p-4 text-sm">
            <Row k="Asset Code" v={wo.asset?.asset_code} />
            <Row k="Asset Name" v={wo.asset?.name} />
            <Row k="Location" v={plan.asset?.location?.name ?? "—"} />
            <Row k="Plan Frequency" v={`${plan.frequency_days} days`} />
            <Row k="Last Done" v={plan.last_done_date ?? "—"} />
            <Row k="Next Due" v={plan.next_due_date} />
            <Row k="Responsible Team" v={plan.team} />
            <Row k="Template" v={plan.template?.name ?? "—"} />
          </CardContent></Card>
        </TabsContent>

        <TabsContent value="checklist" className="mt-3 space-y-3">
          {items.length === 0 ? (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>No checklist items in this template</AlertTitle>
              <AlertDescription>
                No checklist items found in this template. Please add checklist items in{" "}
                <Link to="/admin" search={{ tab: "checklists" }} className="underline">Admin → Checklists</Link>.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              {isReported && (
                <Alert>
                  <PlayCircle className="h-4 w-4" />
                  <AlertDescription>Click <strong>Start PM</strong> above to begin filling the checklist.</AlertDescription>
                </Alert>
              )}
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span>Required items</span><span>{completedRequired} / {requiredCount}</span>
                </div>
                <Progress value={progress} />
              </div>
              {items.map((item) => {
                const r = results[item.id];
                const disabled = !canEditChecklist;
                return (
                  <Card key={item.id} className={cn(r?.completed && "border-emerald-500/50")}>
                    <CardContent className="space-y-2 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-sm">
                          <div className="font-medium">{item.seq}. {item.text}</div>
                          {item.expected && <div className="text-xs text-muted-foreground">Expected: {item.expected}</div>}
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <Badge variant="secondary">{ITEM_TYPE_LABEL[item.item_type] ?? item.item_type}</Badge>
                          {item.is_critical && <Badge variant="destructive">Critical</Badge>}
                          {item.is_required && <Badge variant="outline">Required</Badge>}
                        </div>
                      </div>


                  {item.item_type === "ok_notok" && (
                    <div className="flex gap-2">
                      <Button type="button" size="sm" disabled={disabled} variant={r?.result === "OK" ? "default" : "outline"}
                        onClick={() => updateResult(item.id, { result: "OK", completed: true })}>
                        <CheckCircle2 className="mr-1 h-4 w-4" />OK
                      </Button>
                      <Button type="button" size="sm" disabled={disabled} variant={r?.result === "Not OK" ? "destructive" : "outline"}
                        onClick={() => updateResult(item.id, { result: "Not OK", completed: true })}>
                        <XCircle className="mr-1 h-4 w-4" />Not OK
                      </Button>
                    </div>
                  )}
                  {item.item_type === "value" && (
                    <Input disabled={disabled} placeholder="Enter value" value={r?.value_entered ?? ""}
                      onChange={(e) => updateResult(item.id, { value_entered: e.target.value, completed: !!e.target.value })} />
                  )}
                  {item.item_type === "text" && (
                    <Textarea disabled={disabled} rows={2} placeholder="Enter remarks" value={r?.remarks ?? ""}
                      onChange={(e) => updateResult(item.id, { remarks: e.target.value, completed: !!e.target.value })} />
                  )}
                  {item.item_type === "photo" && (
                    <PhotoCapture woId={woId} disabled={disabled} value={r?.photo_url ?? null}
                      onChange={(url) => updateResult(item.id, { photo_url: url, completed: !!url })} />
                  )}
                  {item.item_type !== "text" && (
                    <Textarea disabled={disabled} rows={1} placeholder="Remarks (optional)" value={r?.remarks ?? ""}
                      onChange={(e) => updateResult(item.id, { remarks: e.target.value })} />
                  )}
                </CardContent>
              </Card>
            );
              })}
            </>
          )}
        </TabsContent>


        <TabsContent value="completion" className="mt-3 space-y-3">
          <Card><CardContent className="space-y-3 p-4">
            <Field label="Date Performed"><Input disabled={isResolved} type="date" value={datePerformed} onChange={(e) => setDatePerformed(e.target.value)} /></Field>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Time Started"><Input disabled={isResolved} type="time" value={timeStarted} onChange={(e) => setTimeStarted(e.target.value)} /></Field>
              <Field label="Time Ended"><Input disabled={isResolved} type="time" value={timeEnded} onChange={(e) => setTimeEnded(e.target.value)} /></Field>
            </div>
            <Field label="Performed By"><Input disabled value={me?.profile?.full_name || me?.user.email || ""} /></Field>
            <Field label="Overall Observations"><Textarea disabled={isResolved} rows={3} value={observations} onChange={(e) => setObservations(e.target.value)} /></Field>
            <Field label="Next Due Date"><Input disabled={isResolved} type="date" value={nextDue} onChange={(e) => setNextDue(e.target.value)} /></Field>
            {criticalAsset && (
              <Field label="Supervisor Sign-off (required for Critical assets)">
                <select disabled={isResolved} className="h-10 w-full rounded-md border bg-background px-3 text-sm" value={supervisorId} onChange={(e) => setSupervisorId(e.target.value)}>
                  <option value="">— Select supervisor —</option>
                  {engineers.map((e) => <option key={e.id} value={e.id}>{e.full_name || e.email}</option>)}
                </select>
              </Field>
            )}
          </CardContent></Card>
        </TabsContent>
      </Tabs>

      <div className="fixed inset-x-0 bottom-0 z-10 border-t bg-background p-3 shadow-lg">
        <div className="mx-auto flex max-w-md items-center gap-2">
          <Link to="/work-orders/$woId" params={{ woId }} className="text-sm text-muted-foreground">Cancel</Link>
          <Button className="ml-auto" onClick={handleSubmit} disabled={submit.isPending || isResolved || isReported}>
            {submit.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
            {isResolved ? "Submitted" : "Submit PM"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return <div className="flex justify-between gap-2"><span className="text-muted-foreground">{k}</span><span className="text-right">{v ?? "—"}</span></div>;
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="space-y-1"><Label className="text-xs text-muted-foreground">{label}</Label>{children}</div>;
}
function BackBar({ onBack }: { onBack: () => void }) {
  return (
    <Button variant="ghost" size="sm" onClick={onBack}>
      <ArrowLeft className="mr-1 h-4 w-4" />Back to WO
    </Button>
  );
}
function StatusPanel({ label }: { label: string }) {
  return (
    <Card className="mx-auto mt-8 max-w-lg">
      <CardContent className="flex items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>{label}</span>
      </CardContent>
    </Card>
  );
}
function Notice({ icon, title, body, action }: { icon: React.ReactNode; title: string; body: string; action?: React.ReactNode }) {
  return (
    <Card className="mx-auto mt-8 max-w-lg">
      <CardContent className="space-y-3 p-6 text-center">
        <div className="flex justify-center">{icon}</div>
        <h2 className="text-lg font-semibold">{title}</h2>
        <p className="text-sm text-muted-foreground">{body}</p>
        {action}
      </CardContent>
    </Card>
  );
}

type WoLike = { id: string; wo_number: string; type: string; status: string; pm_plan_id: string | null; assigned_to: string | null };
type PlanLike = { id: string; checklist_template_id: string | null; template?: { name: string } | null } | null;
function DebugPanel({ show, wo, plan, itemsCount, me }: { show: boolean; wo: WoLike; plan: PlanLike; itemsCount: number; me: { user: { id: string }; roles: string[] } }) {
  if (!show) return null;
  return (
    <Collapsible>
      <Card>
        <CollapsibleTrigger asChild>
          <button className="flex w-full items-center justify-between gap-2 p-3 text-left text-xs text-muted-foreground hover:bg-muted">
            <span className="inline-flex items-center gap-1"><Bug className="h-3 w-3" /> PM Debug</span>
            <ChevronDown className="h-3 w-3" />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-1 p-3 pt-0 font-mono text-[11px]">
            <DebugRow k="WO ID" v={wo.id} />
            <DebugRow k="WO Number" v={wo.wo_number} />
            <DebugRow k="WO Type" v={wo.type} />
            <DebugRow k="WO Status" v={wo.status} />
            <DebugRow k="PM Plan ID" v={wo.pm_plan_id ?? "—"} />
            <DebugRow k="Checklist Template ID" v={plan?.checklist_template_id ?? "—"} />
            <DebugRow k="Checklist Template" v={plan?.template?.name ?? "—"} />
            <DebugRow k="Checklist Items Count" v={String(itemsCount)} />
            <DebugRow k="Assigned To" v={wo.assigned_to ?? "—"} />
            <DebugRow k="Current User ID" v={me.user.id} />
            <DebugRow k="Current User Roles" v={me.roles.join(", ") || "—"} />
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
function DebugRow({ k, v }: { k: string; v: string }) {
  return <div className="flex justify-between gap-2"><span className="opacity-70">{k}</span><span className="text-right">{v}</span></div>;
}

function LinkPlanDialog({ open, onOpenChange, woId, assetId }: { open: boolean; onOpenChange: (v: boolean) => void; woId: string; assetId: string }) {
  const { data: plans = [], isLoading } = useActivePlansForAsset(open ? assetId : null);
  const linkMut = useLinkPmPlanToWo();
  const [planId, setPlanId] = useState<string>("");
  useEffect(() => { if (open) setPlanId(""); }, [open]);
  const onSave = async () => {
    if (!planId) { toast.error("Select a PM plan"); return; }
    try {
      await linkMut.mutateAsync({ wo_id: woId, plan_id: planId });
      toast.success("PM plan linked");
      onOpenChange(false);
    } catch (e) { toast.error((e as Error).message); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Link PM Plan</DialogTitle></DialogHeader>
        {isLoading ? (
          <div className="grid place-items-center py-6"><Loader2 className="h-4 w-4 animate-spin" /></div>
        ) : plans.length === 0 ? (
          <p className="text-sm text-muted-foreground">No active PM plans exist for this asset. Create one in Preventive Maintenance first.</p>
        ) : (
          <div className="space-y-2">
            {plans.map((p) => (
              <label key={p.id} className={cn("flex cursor-pointer items-start gap-2 rounded border p-2 text-sm", planId === p.id && "border-primary")}>
                <input type="radio" className="mt-1" checked={planId === p.id} onChange={() => setPlanId(p.id)} />
                <div className="flex-1">
                  <div className="font-medium">Every {p.frequency_days}d · Next due {p.next_due_date}</div>
                  <div className="text-xs text-muted-foreground">Template: {p.template?.name ?? "Not configured"}</div>
                </div>
              </label>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} disabled={linkMut.isPending || !planId}>
            {linkMut.isPending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Link Plan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PhotoCapture({ woId, value, onChange, disabled }: { woId: string; value: string | null; onChange: (url: string | null) => void; disabled?: boolean }) {
  const [uploading, setUploading] = useState(false);
  const handle = async (file: File) => {
    setUploading(true);
    try {
      const sb = getSupabase();
      const path = `pm/${woId}/${crypto.randomUUID()}-${file.name}`;
      const { error } = await sb.storage.from("wo-photos").upload(path, file, { upsert: false });
      if (error) throw error;
      onChange(path);
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setUploading(false);
    }
  };
  return (
    <div className="space-y-1">
      <label className={cn("flex items-center gap-2 rounded border border-dashed p-2 text-xs", disabled && "opacity-50")}>
        <Camera className="h-4 w-4" />
        {uploading ? "Uploading…" : value ? "Replace photo" : "Take / upload photo"}
        <input type="file" accept="image/*" capture="environment" className="hidden" disabled={disabled}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handle(f); }} />
      </label>
      {value && <div className="truncate text-[10px] text-muted-foreground">{value.split("/").pop()}</div>}
    </div>
  );
}
